/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'

import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Cell, flexRender } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  Cell as TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
} from '@enechain/ecloud-designsystem'

interface BaseRowData {
  allowDeal: boolean
}

type Props<T extends BaseRowData> = {
  data: T[]
  columns: Array<ColumnDef<T, any>>
}

export const AvailableDealBaseTable = <T extends BaseRowData>({
  data,
  columns,
}: Props<T>): React.ReactElement | null => {
  const { t } = useTranslation(['page', 'common', 'domain'])

  const tableConfig = {
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  }

  const table = useReactTable<T>(
    tableConfig as Parameters<typeof useReactTable<T>>[0],
  )

  const addCellStyleFunc = (
    cell: Cell<T, unknown>,
  ): { backgroundColor: string; color: string } => {
    const row = cell.row.original

    return {
      backgroundColor: !row.allowDeal ? 'Surface.negativeLow' : 'transparent',
      color: !row.allowDeal ? 'Text.negativeMid' : 'inherit',
    }
  }

  if (data.length === 0) {
    return null
  }

  const tableContent = (
    <TableScrollArea>
      <TableRoot
        css={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          tableLayout: 'auto',
        }}
        width="100%"
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta
                return (
                  <TableColumnHeader
                    key={header.id}
                    css={{
                      paddingX: '2',
                      paddingY: '2',
                      ...meta?.headerStyle,
                    }}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableColumnHeader>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ||
          table.getRowModel().rows == null ? (
            <TableRow>
              <TableCell colSpan={table.getHeaderGroups()[0].headers.length}>
                <Box width="100%" textAlign="center">
                  {t('common:error.empty_data')}
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row, index) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta
                  const isOrderRelationColumn =
                    cell.column.id === 'orderRelationString' ||
                    cell.column.id === 'allowDealStatus'

                  return (
                    <TableCell
                      key={cell.id}
                      css={{
                        paddingX: '2',
                        paddingY: '2',
                        ...meta?.cellStyle,
                        ...(!isOrderRelationColumn
                          ? addCellStyleFunc(cell)
                          : {}),
                      }}
                      isLast={table.getRowModel().rows.length - 1 === index}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </TableRoot>
    </TableScrollArea>
  )

  return tableContent
}
